import { Component, DestroyRef, NgZone, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { Integration } from '../../../../../models/integration/integration';
import {
  Crawler, CrawlerLaunchEligibility,
  CrawlerStatus,
  CrawlerType,
  ACTIVE_CRAWLER_STATUSES,
} from '../../../../../models/integration/crawler';
import { Territory } from '../../../../../models/territory';
import { TerritoryService } from '../../../../../services/territory.service';
import {
  ConfirmationDialogComponent,
} from '../../../../../dialogs/confirmation-dialog/confirmation-dialog.component';
import {
  CrawlerDialogComponent,
} from '../../../../../dialogs/crawler-dialog/crawler-dialog.component';
import {
  CrawlerConfigDialogComponent,
} from '../../../../../dialogs/crawler-config-dialog/crawler-config-dialog.component';
import {
  IntegrationRunStep,
  IntegrationRunStepKey,
  STEPS_BY_CRAWLER_TYPE,
  STEP_LABEL_KEYS, IntegrationRunStepStatus,
} from '../../../../../shared/single-crawler/single-crawler.steps';
import {
  SingleCrawlerCountry,
} from './integration-summary-card/single-crawler-summary-card.component';
import {
  IntegrationService,
} from '../../../../../services/integration.service';
import {
  LaunchEligibilityPoller,
  isCrawlerActive,
} from '../../../../../shared/single-crawler/launch-eligibility';
import { UtilsService } from '../../../../../services/utils.service';

// combines a single view for all crawler types (single url, excel and discovery).
@Component({
  selector: 'app-single-crawler',
  templateUrl: './single-crawler.component.html',
  styleUrls: ['./single-crawler.component.scss'],
})
export class SingleCrawlerComponent implements OnInit {
  loading = false;
  done = false;

  integration?: Integration;
  crawler?: Crawler;

  // Live status for the summary card, updated by the eligibility poll while a
  // discovery/crawl is running (falls back to the snapshot via loadIntegration).
  liveStatus?: CrawlerStatus;

  steps: IntegrationRunStep[] = [];
  activeStepKey: IntegrationRunStepKey = IntegrationRunStepKey.CRAWL;

  // `discovery` tells whether a discovery produced at least one non-base URL,
  // `crawl` whether a crawl produced a document. Used to mark steps completed
  // and to unlock navigation.
  stepGate = {
    discovery: false,
    crawl: false,
  };

  // Cached once on load so the template never receives a fresh array/string
  // reference on every change-detection cycle (which would loop forever).
  summaryCountries: SingleCrawlerCountry[] = [];
  summaryFrequencyLabel = '';

  // Polls the crawler's launch eligibility to keep the summary card status
  // live, then syncs the step gates on every emission. Same cadence + same
  // switchMap drop pattern as the step pollers.
  readonly eligibilityPoller: LaunchEligibilityPoller;

  readonly IntegrationRunStepKey = IntegrationRunStepKey;
  readonly CrawlerType = CrawlerType;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly integrationService: IntegrationService,
    private readonly toastr: ToastrService,
    private readonly translate: TranslateService,
    private readonly territoryService: TerritoryService,
    private readonly router: Router,
    private readonly dialog: MatDialog,
    private readonly utils: UtilsService,
    private readonly destroyRef: DestroyRef,
    zone: NgZone,
  ) {
    // Fetch immediately, then keep polling while a discovery/crawl is active
    // or a launch is pending; inclusive takeWhile keeps the latest settled
    // status on the summary card once the work finishes.
    this.eligibilityPoller = new LaunchEligibilityPoller(
      this.integrationService,
      this.destroyRef,
      zone,
      (eligibility) => {
        // Right after a launch the queue has not flipped the status yet: the previous run's final status would
        // show (COMPLETED while the crawl is starting).
        this.liveStatus = this.eligibilityPoller.launchPending.isPending && !isCrawlerActive(eligibility.status)
          ? CrawlerStatus.PENDING
          : eligibility.status;
        this.syncStepsFromEligibility(eligibility);
      },
    );
  }

  ngOnInit(): void {
    firstValueFrom(this.route.params)
      .then(async (params) => {
        const id = params['id'] as string;

        this.loading = true;
        this.done = false;
        await this.loadIntegration(id);
        this.loading = false;
        this.done = true;
        this.eligibilityPoller.start(id);
      });
    moment.locale(this.translate.currentLang);
  }

  // Fired by the crawl/discovery steps once their launch POST resolved
  onRunLaunched(): void {
    // Optimistic: show the summary as queued/running right away; the poll
    // takes over from the real eligibility as soon as the queue flips status.
    this.liveStatus = CrawlerStatus.PENDING;
    this.eligibilityPoller.launchPending.mark();
    if (this.integration) {
      this.eligibilityPoller.start(this.integration._id);
    }
  }

  // The crawl can only be canceled while it is actively running. During the
  // discovery phase (crawler-scoped, no execution row) lastExecutionId is not
  // required
  canCancel(): boolean {
    const crawler = this.crawler!;
    if (!ACTIVE_CRAWLER_STATUSES.has(crawler.progress?.status)) return false;
    const discoveryPhase = this.eligibilityPoller.eligibility?.isDiscoveryRunning || false;
    return discoveryPhase || !!crawler.lastExecutionId;
  }

  async cancel(): Promise<void> {
    const crawler = this.crawler;
    const integration = this.integration;
    if (!integration || !crawler || !this.canCancel()) return;

    const isDiscoveryPhase = this.eligibilityPoller.eligibility?.isDiscoveryRunning || false;

    const config: MatDialogConfig = {
      panelClass: 'dialog-container',
      width: '480px',
      data: {
        title: 'INTEGRATIONS.CANCEL_CRAWLER_TITLE',
        text: this.translate.instant('INTEGRATIONS.CANCEL_CRAWLER_TEXT', integration),
        // the dialog's own dismiss button already says Cancel: the confirmation says what it stops
        button: {
          text: isDiscoveryPhase ? 'INTEGRATIONS.BACKLINK_CRAWLER.CANCEL_TOOLTIP' : 'INTEGRATIONS.CRAWL_URLS.CANCEL_TOOLTIP',
          class: 'danger-button',
        },
      }
    };
    const dialog = this.dialog.open(ConfirmationDialogComponent, config);
    const result: { confirmed: boolean } = await firstValueFrom(dialog.afterClosed());
    if (result?.confirmed && (isDiscoveryPhase || crawler.lastExecutionId)) {
      try {
        if (isDiscoveryPhase) {
          await this.integrationService.cancelDiscovery(integration._id);
        } else {
          await this.integrationService.cancelExecution(
            integration._id,
            crawler.lastExecutionId!,
          );
        }
        this.toastr.success(this.translate.instant('INTEGRATIONS.CANCEL_CRAWLER_SUCCESS'));
        await this.loadIntegration(integration._id);
      } catch (err) {
        this.utils.toastError(err);
      }
    }
  }

  async viewCrawlerConfig(): Promise<void> {
    const crawler = this.crawler;
    if (!crawler) return;
    const config: MatDialogConfig = {
      panelClass: 'dialog-container',
      height: '600px',
      maxHeight: '600px',
      width: '600px',
      autoFocus: false,
      data: { integration: { integration: this.integration, crawler } }
    };
    this.dialog.open(CrawlerConfigDialogComponent, config);
  }

  async edit(): Promise<void> {
    const crawler = this.crawler;
    if (!crawler) return;
    const config: MatDialogConfig = {
      panelClass: 'dialog-container',
      width: '480px',
      autoFocus: false,
      data: { mode: 'edit', integration: { integration: this.integration, crawler } }
    };
    const dialog = this.dialog.open(CrawlerDialogComponent, config);
    const result: { created: boolean; edited: boolean } = await firstValueFrom(dialog.afterClosed());
    if (result?.edited) {
      await this.loadIntegration(crawler.integrationId);
    }
  }

  async delete(): Promise<void> {
    const integration = this.integration;
    if (!integration) return;
    const config: MatDialogConfig = {
      panelClass: 'dialog-container',
      width: '480px',
      data: {
        title: 'INTEGRATIONS.DELETE_INTEGRATION_TITLE',
        text: this.translate.instant('INTEGRATIONS.DELETE_INTEGRATION_TEXT', integration),
        button: { text: 'ACTIONS.DELETE', class: 'danger-button' },
        confirmation: { text: 'INTEGRATIONS.DELETE_INTEGRATION_CONFIRMATION', value: integration.name }
      }
    };
    const dialog = this.dialog.open(ConfirmationDialogComponent, config);
    const result: { confirmed: boolean } = await firstValueFrom(dialog.afterClosed());
    if (result?.confirmed) {
      try {
        await this.integrationService.delete(integration._id);
        this.toastr.success(this.translate.instant('INTEGRATIONS.INTEGRATION_DELETED', { name: integration.name }));
        await this.router.navigate(['..'], { relativeTo: this.route });
      } catch (err) {
        this.utils.toastError(err);
      }
    }
  }

  private async loadIntegration(id: string): Promise<void> {
    try {
      const {
        integration,
        crawler,
      } = await this.integrationService.retrieveIntegration(id);
      this.integration = integration;
      this.crawler = crawler;
      // Seed the summary card with the snapshot before the poll takes over.
      this.liveStatus = crawler.progress?.status;
      const eligibility = await this.integrationService.retrieveLaunchEligibility(id);
      this.stepGate = {
        discovery: eligibility.discoveryGate,
        crawl: eligibility.crawlGate,
      };
      this.rebuildSteps();
      this.activeStepKey = this.currentStep();
      this.summaryCountries = this.computeCountries();
      this.summaryFrequencyLabel = this.computeFrequencyLabel();
    } catch (err: unknown) {
      if (err instanceof HttpErrorResponse && err.status === 404) {
        void this.router.navigate(['errors', '404']);
        return;
      }
      this.toastr.error(this.translate.instant('ERRORS.GENERIC'));
    }
  }

  private stepStatus(key: IntegrationRunStepKey): IntegrationRunStepStatus {
    if (this.isStepDone(key)) return IntegrationRunStepStatus.COMPLETED;
    if (key === this.activeStepKey) return IntegrationRunStepStatus.IN_PROGRESS;
    return IntegrationRunStepStatus.PENDING;
  }

  /**
   * Discovery step once a discovery yielded a non-base URL, the Crawl step once
   * a crawl yielded a document. Analyze has no gate of its own.
   */
  private isStepDone(key: IntegrationRunStepKey): boolean {
    switch (key) {
      case IntegrationRunStepKey.DISCOVERY:
        return this.stepGate.discovery;
      case IntegrationRunStepKey.CRAWL:
        return this.stepGate.crawl;
      default:
        return false;
    }
  }

  private isStepUnlocked(
    key: IntegrationRunStepKey,
    order: IntegrationRunStepKey[],
  ): boolean {
    const index = order.indexOf(key);
    if (index <= 0) return true;
    return order
      .slice(
        0,
        index,
      )
      .every((previous) => this.isStepDone(previous));
  }

  // determine what the current step is based on crawler type and current gate
  private currentStep(): IntegrationRunStepKey {
    if (this.crawler?.type === CrawlerType.DISCOVERY) {
      if (!this.stepGate.discovery) return IntegrationRunStepKey.DISCOVERY;
      if (!this.stepGate.crawl) return IntegrationRunStepKey.CRAWL;
      return IntegrationRunStepKey.ANALYZE;
    }
    // analyze available only if crawl step yielded some docs
    if (this.stepGate.crawl) return IntegrationRunStepKey.ANALYZE;
    return IntegrationRunStepKey.CRAWL;
  }

  /**
   * Lifecycle of the wizard's step gates, refreshed from each eligibility
   * emission without rebuilding the steps array. Both gates come from the
   * backend eligibility:
   *  - discoveryGate: a discovery ran and yielded a non-base URL.
   *  - crawlGate: a crawl ran and produced at least one document.
   */
  private syncStepsFromEligibility(eligibility: CrawlerLaunchEligibility): void {
    const {
      discoveryGate,
      crawlGate,
    } = eligibility;
    // rebuild even when gates are unchanged if the stepper is still empty
    const stepsMissing = this.steps.length === 0;
    const gatesUnchanged =
      discoveryGate === this.stepGate.discovery
      && crawlGate === this.stepGate.crawl;
    if (!stepsMissing && gatesUnchanged) {
      return;
    }
    this.stepGate = {
      discovery: discoveryGate,
      crawl: crawlGate,
    };
    this.rebuildSteps();

    if (stepsMissing) {
      this.activeStepKey = this.currentStep();
    }
  }

  private rebuildSteps(): void {
    if (!this.crawler) return;
    const order = STEPS_BY_CRAWLER_TYPE[this.crawler.type];
    this.steps = order.map((key) => ({
      key,
      labelKey: STEP_LABEL_KEYS[key],
      status: this.stepStatus(key),
      selectable: this.isStepUnlocked(
        key,
        order,
      ),
    }));
  }

  get activeStepIndex(): number {
    return this.steps.findIndex((s) => s.key === this.activeStepKey);
  }

  selectStep(step: IntegrationRunStep): void {
    this.activeStepKey = step.key;
  }

  private computeFrequencyLabel(): string {
    if (!this.crawler) return '';
    const created = this.integration?.createdAt
      ? moment(this.integration.createdAt)
        .format('D MMMM YYYY')
      : '';
    const recurrence = this.crawler.recurrence?.toUpperCase();
    const frequency = recurrence
      ? this.translate.instant(`DATES.${recurrence}`)
      : '';
    const createdAt = created ? ` · ${created}` : '';
    return `${frequency}${createdAt}`;
  }

  private computeCountries(): SingleCrawlerCountry[] {
    const territories: Territory[] = this.territoryService.territories;
    return (this.crawler?.countryCodes ?? []).map((code) => ({
      code,
      name: this.countryName(
        code,
        territories,
      ),
    }));
  }

  private countryName(code: string, territories: Territory[]): string {
    const territory = territories.find(
      (t) => t.code.toUpperCase() === code
        || t.alternateCodes.some((c) => c.toUpperCase() === code),
    );
    if (territory) {
      const name = this.translate.instant(`TERRITORIES.${territory.code.toUpperCase()}`);
      if (name && name !== `TERRITORIES.${territory.code.toUpperCase()}`) {
        return name;
      }
    }
    return code;
  }

  get subtitle(): string {
    if (!this.crawler || !this.integration) return '';
    return this.computeSubtitle();
  }

  private computeSubtitle(): string {
    const date = this.integration?.createdAt
      ? moment(this.integration.createdAt)
        .format('ll')
      : '-';
    const type = this.translate.instant(`INTEGRATIONS.TYPE_${this.crawler?.type}`);
    const owner = this.integration?.owner
      ? `${this.integration.owner.firstName} ${this.integration.owner.lastName}`
      : '-';

    return this.translate.instant(
      'INTEGRATIONS.SUBTITLE',
      {
        type,
        date,
        owner,
      },
    );
  }

  get searchDepth(): number | undefined {
    if (!this.crawler || this.crawler.type !== CrawlerType.DISCOVERY) {
      return undefined;
    }
    return this.crawler.spec.discoveryDepth;
  }

  get specUrl(): string | undefined {
    if (!this.crawler || this.crawler.type === CrawlerType.EXCEL_BATCH) {
      return undefined;
    }
    return this.crawler.spec.url;
  }

  get maxPages(): number | undefined {
    if (!this.crawler || this.crawler.type !== CrawlerType.DISCOVERY) {
      return undefined;
    }
    return this.crawler.spec.maxDiscoverablePages;
  }
}
